/* COMPARE, on the made-to-order jewelry pages.
   Owner rule 2026-08-06: the jewelry collections get the tools the gemstone pages
   already have. Sibling of the compare half of `stone-list.js`.

   ── WHY THIS IS A SEPARATE FILE AND NOT A FLAG ON stone-list.js ─────────────
   The two compare WHAT IS THERE, and almost nothing is shared. A stone has carat,
   millimetre size, origin, treatment and a certificate, and its overlay draws
   silhouettes at true relative size so a 2 ct visibly outsizes a 1 ct. A ring has
   none of those and no size to draw; it has a metal set, a gem set, a formation and
   a price. Forking stone-list.js to serve both would mean a file where half the rows
   are dead on any given page — and stone-list.js is 631 lines of live, working code
   on 22 species pages, so editing it to add jewelry is pure downside risk.

   WHAT IS SHARED IS THE LOOK: this reuses stone-list.css's `.sc__*` overlay classes,
   so the two comparisons are the same object to a shopper. `jewelry-browse.liquid`
   loads that stylesheet for exactly this reason.

   ⚠️ THE STORAGE KEY IS DELIBERATELY NOT `sr.compare.v1`. That is the stone tray's.
   Sharing it would let a shopper tick two sapphires and a ring into one overlay whose
   rows are carat and origin, and the ring would be three dashes. Separate key,
   separate tray, and a jewelry page never sees the stone selection.

   ── PROGRESSIVE ENHANCEMENT, SAME CONTRACT AS THE REST OF THE PANEL ─────────
   `card-jewelry-compare.liquid` renders the tick-box `hidden`. This file reveals it.
   So with JS blocked or broken the shopper sees no compare affordance at all, rather
   than a control that silently stores nothing. Do not "fix" that by rendering it
   visible — a dead tick-box is worse than an absent one. */
(function () {
  'use strict';

  // ⚠️ AN SVG, NOT A "×" GLYPH. `.sct-item__x` and `.sc__x` are round buttons styled
  // around `svg { width: 1rem }`; a text × inherits the font's own bearings and sits
  // visibly off-centre, and the close button renders smaller than the stone one.
  var X_SVG = '<svg viewBox="0 0 12 12" aria-hidden="true"><path d="M2 2l8 8M10 2l-8 8"/></svg>';

  var KEY = 'sr.jcompare.v1';
  var MAX = 4;                       // 4 columns is what the overlay grid fits
  var doc = document;
  var picked = [];
  var tray, trayItems, trayHint, trayGo, dlg, dlgOnly, dlgScroll, dlgTitle;

  try {
    picked = JSON.parse(localStorage.getItem(KEY) || '[]');
    if (!Array.isArray(picked)) picked = [];
  } catch (e) { picked = []; }        // private mode: compare works, just not sticky

  function save() {
    try { localStorage.setItem(KEY, JSON.stringify(picked)); } catch (e) { /* ditto */ }
  }

  function el(tag, cls, text) {
    var n = doc.createElement(tag);
    if (cls) n.className = cls;
    if (text != null) n.textContent = text;
    return n;
  }

  function boxes() { return doc.querySelectorAll('.jc-cmp__i'); }

  function payloadOf(input) {
    try { return JSON.parse(input.dataset.cmp || 'null'); } catch (e) { return null; }
  }

  function indexOf(id) {
    for (var i = 0; i < picked.length; i++) if (picked[i].id === id) return i;
    return -1;
  }

  /* Re-tick every box from storage, and close the gate at the cap. Runs on load AND
     after infinite scroll appends cards, which arrive as fresh markup that has never
     seen the selection. */
  function sync() {
    var full = picked.length >= MAX;
    Array.prototype.forEach.call(boxes(), function (input) {
      var p = payloadOf(input);
      if (!p) return;
      var on = indexOf(p.id) !== -1;
      input.checked = on;
      input.disabled = full && !on;
      var host = input.closest('.jc-cmp');
      if (host) { host.hidden = false; host.classList.toggle('is-on', on); }
    });
    renderTray();
  }

  function toggle(input) {
    var p = payloadOf(input);
    if (!p) return;
    var at = indexOf(p.id);
    if (at !== -1) picked.splice(at, 1);
    else {
      if (picked.length >= MAX) { input.checked = false; return; }
      picked.push(p);
    }
    save();
    sync();
    if (dlg && dlg.open) paint();
  }

  function remove(id) {
    var at = indexOf(id);
    if (at !== -1) { picked.splice(at, 1); save(); sync(); }
    if (dlg && dlg.open) { picked.length >= 2 ? paint() : dlg.close(); }
  }

  /* ---------------------------------------------------------------- the tray ---- */
  /* ⚠️ THE STRUCTURE IS NOT OPTIONAL. stone-list.css styles `.sct > .sct__in >
     (.sct__items, .sct__acts)` and hides the bar with `.sct[hidden]`, and it needs
     `body.sct-open` to make room for itself. The first version of this file invented
     its own class names and skipped the inner wrapper: the tray rendered unstyled and
     clipped in the bottom-left corner of the page. Match the markup, do not restyle. */
  function buildTray() {
    tray = el('div', 'sct');
    tray.hidden = true;
    var inner = el('div', 'sct__in');
    trayItems = el('div', 'sct__items');
    var acts = el('div', 'sct__acts');
    trayHint = el('span', 'sct__hint');
    trayGo = el('button', 'sct__go');
    trayGo.type = 'button';
    trayGo.addEventListener('click', openDialog);
    var clear = el('button', 'sct__clear', 'Clear');
    clear.type = 'button';
    clear.addEventListener('click', function () {
      picked = []; save(); sync(); if (dlg && dlg.open) dlg.close();
    });
    acts.appendChild(trayHint);
    acts.appendChild(trayGo);
    acts.appendChild(clear);
    inner.appendChild(trayItems);
    inner.appendChild(acts);
    tray.appendChild(inner);
    doc.body.appendChild(tray);
  }

  function renderTray() {
    if (!tray) buildTray();
    // The body makes room while the bar is up — a fixed bar over the last row of
    // cards reads as a broken page, not as a tray.
    doc.body.classList.toggle('sct-open', picked.length > 0);
    if (!picked.length) {
      tray.classList.remove('is-open');
      tray.hidden = true;
      return;
    }
    tray.hidden = false;
    // `is-open` plays the slide-in ONCE — adding a second piece must not re-run it.
    if (!tray.classList.contains('is-open')) tray.classList.add('is-open');

    trayItems.textContent = '';
    picked.forEach(function (p) {
      var chip = el('div', 'sct-item');
      if (p.img) {
        var im = doc.createElement('img');
        im.src = p.img; im.alt = ''; im.loading = 'lazy';
        chip.appendChild(im);
      }
      chip.appendChild(el('span', 'sct-item__t', p.t));
      var x = el('button', 'sct-item__x');
      x.innerHTML = X_SVG;
      x.type = 'button';
      x.setAttribute('aria-label', 'Remove ' + p.t + ' from compare');
      x.addEventListener('click', function () { remove(p.id); });
      chip.appendChild(x);
      trayItems.appendChild(chip);
    });
    // The remaining SLOTS, dashed circles — `.sct-slot`. They are what tells a
    // shopper the tray holds four, and the stone tray has always drawn them.
    for (var i = picked.length; i < MAX; i++) {
      trayItems.appendChild(el('div', 'sct-slot'));
    }
    trayHint.textContent = picked.length < 2 ? 'Pick another to compare' : '';
    trayGo.textContent = 'Compare (' + picked.length + ')';
    trayGo.disabled = picked.length < 2;
  }

  /* ------------------------------------------------------------- the overlay ---- */
  function buildDialog() {
    dlg = doc.createElement('dialog');
    dlg.className = 'sc';
    var inner = el('div', 'sc__in');

    var top = el('header', 'sc__top');
    dlgTitle = el('h2', 'sc__h');

    var opts = el('div', 'sc__opts');
    // "Differences only" — the stone overlay has it and the owner asked for the two
    // to operate identically. It toggles `is-diffonly` on the dialog; stone-list.css
    // then hides every `.sc__row.is-same`, which is why `addRow` must keep marking
    // them. Without this the checkbox exists and does nothing.
    var only = el('label', 'sc__only');
    dlgOnly = doc.createElement('input');
    dlgOnly.type = 'checkbox';
    dlgOnly.addEventListener('change', function () {
      dlg.classList.toggle('is-diffonly', dlgOnly.checked);
    });
    only.appendChild(dlgOnly);
    only.appendChild(el('span', null, 'Differences only'));

    var x = el('button', 'sc__x');
      x.innerHTML = X_SVG;
    x.type = 'button';
    x.setAttribute('aria-label', 'Close compare');
    x.addEventListener('click', function () { dlg.close(); });

    opts.appendChild(only);
    opts.appendChild(x);
    top.appendChild(dlgTitle);
    top.appendChild(opts);

    dlgScroll = el('div', 'sc__scroll');
    inner.appendChild(top);
    inner.appendChild(dlgScroll);
    dlg.appendChild(inner);
    dlg.addEventListener('click', function (e) { if (e.target === dlg) dlg.close(); });
    doc.body.appendChild(dlg);
  }

  /* A row is "the same" when every piece reads identically, compared on the RENDERED
     string — that is what the shopper is being asked to compare, so "Natural" and
     "natural" are not a difference. Same rows get dimmed so the differences carry. */
  function allSame(values) {
    for (var i = 1; i < values.length; i++) {
      if (String(values[i]).toLowerCase() !== String(values[0]).toLowerCase()) return false;
    }
    return true;
  }

  function addRow(key, values, opts) {
    opts = opts || {};
    var row = el('div', 'sc__row');
    row.appendChild(el('div', 'sc__k', key));
    values.forEach(function (v, i) {
      var cell = el('div', 'sc__v');
      if (v && v.nodeType) cell.appendChild(v);
      else cell.textContent = v == null || v === '' ? '—' : v;
      if (opts.best === i) cell.appendChild(el('span', 'sc__best', opts.bestLabel));
      row.appendChild(cell);
    });
    if (!opts.neverSame && allSame(values.map(function (v) {
      return v == null ? '' : (v.nodeType ? v.textContent : v);
    }))) row.classList.add('is-same');
    dlgScroll.appendChild(row);
    return row;
  }

  /* Index of the single lowest price, or -1 on a tie / incomplete data. Only ever
     LOWEST: on jewelry "best" is not a judgement about quality, and marking anything
     else would be the store telling a shopper which piece to prefer. */
  function cheapestIndex(nums) {
    var at = -1, best = null, ties = 0;
    nums.forEach(function (n, i) {
      if (!n) return;
      if (best === null || n < best) { best = n; at = i; ties = 1; }
      else if (n === best) ties++;
    });
    return ties === 1 && nums.filter(Boolean).length === nums.length ? at : -1;
  }

  function paint() {
    dlgScroll.textContent = '';
    dlg.style.setProperty('--sc-n', picked.length);
    dlgTitle.textContent = 'Compare ' + picked.length +
      (picked.length === 1 ? ' piece' : ' pieces');

    /* ---- head: photo, title, price, remove ----
       ⚠️ THE CLASS NAMES ARE NOT INTERCHANGEABLE. `.sc-head__m` is the MEDIA box —
       stone-list.css gives it a fixed height for the photo — `.sc-head__t` is the
       title link and `.sc-head__p` the price. The first version of this file used
       `__p` for the photo and `__m` for the name, which rendered the product name
       inside the media box and left a tall empty rectangle under every image. */
    var heads = picked.map(function (p) {
      var box = el('div', 'sc-head');

      var media = el('a', 'sc-head__m');
      media.href = p.u;
      if (p.img) {
        var img = doc.createElement('img');
        img.src = p.img;
        img.alt = p.t;
        img.loading = 'lazy';
        media.appendChild(img);
      }
      box.appendChild(media);

      var a = el('a', 'sc-head__t', p.t);
      a.href = p.u;
      box.appendChild(a);
      box.appendChild(el('span', 'sc-head__p', p.p));

      // Remove only while a comparison would survive it: the overlay never opens on
      // fewer than two, so removing one of two must not leave a one-piece compare.
      if (picked.length > 2) {
        var rm = el('button', 'sc-head__x');
        rm.innerHTML = X_SVG + '<span>Remove</span>';
        rm.type = 'button';
        rm.setAttribute('aria-label', 'Remove ' + p.t + ' from compare');
        rm.addEventListener('click', function () { remove(p.id); });
        box.appendChild(rm);
      }

      // Never "same": the header carries each piece's own identity, so dimming it
      // when two share a price would hide the photos the comparison is built on.
      box.dataset.cmp = p.id;
      return box;
    });
    addRow('', heads, { neverSame: true });

    addRow('Price', picked.map(function (p) { return p.p; }), {
      best: cheapestIndex(picked.map(function (p) { return parseFloat(p.pn) || 0; })),
      bestLabel: 'lowest'
    });
    addRow('Type', picked.map(function (p) { return p.ty; }));
    addRow('Stone', picked.map(function (p) { return p.g; }));
    addRow('Metals', picked.map(function (p) { return p.m; }));

    // Formation only earns a row when at least one piece has it — an all-dashes row
    // is noise, and most plain-metal pieces have no formation at all.
    if (picked.some(function (p) { return p.f; })) {
      addRow('Formation', picked.map(function (p) { return p.f; }));
    }
  }

  function openDialog() {
    if (!picked.length) return;
    if (!dlg) buildDialog();
    paint();
    if (typeof dlg.showModal === 'function') dlg.showModal();
    else dlg.setAttribute('open', '');
  }

  /* Delegated, so cards appended by infinite scroll work with no re-binding. */
  doc.addEventListener('change', function (e) {
    if (e.target && e.target.classList && e.target.classList.contains('jc-cmp__i')) {
      toggle(e.target);
    }
  });

  /* Infinite scroll appends `.grid__item`s that have never seen the selection, so
     re-sync when the grid's children change. Same reason stone-list.js observes. */
  function observe() {
    var grid = doc.getElementById('product-grid');
    if (!grid || !window.MutationObserver) return;
    new MutationObserver(function () { sync(); }).observe(grid, { childList: true });
  }

  function boot() {
    if (!doc.querySelector('.jc-cmp')) return;   // not a jewelry-browse page
    sync();
    observe();
  }

  if (doc.readyState === 'loading') doc.addEventListener('DOMContentLoaded', boot);
  else boot();
})();
